import { useMutation } from '@apollo/client';
import type {
  UpdateIssueAssessmentMutation,
  UpdateIssueAssessmentMutationVariables,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  namedOperations,
  UpdateIssueAssessmentDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { UpdateIssueAssessmentTRPCInput } from './useUpdateIssueAssessmentTRPC';
import { useUpdateIssueAssessmentTRPC } from './useUpdateIssueAssessmentTRPC';

const coerceToArray = (value: string | string[] | undefined): string[] =>
  Array.isArray(value) ? value : value != null ? [value] : [];

export const useUpdateIssueAssessment = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation (legacy)
  const [updateIssueAssessmentGraphQL, graphqlState] = useMutation(
    UpdateIssueAssessmentDocument,
    {
      update: (cache) => {
        evictField(cache, 'issue');
        evictField(cache, 'tag_type');
        evictField(cache, 'issue_assessment');
        evictField(cache, 'issue_parent');
        evictField(cache, 'control');
        evictField(cache, 'issue_assessment_aggregate');
        evictField(cache, 'issue_assessment_audit');
      },
      refetchQueries: [namedOperations.Query.getIssueById],
    }
  );

  // tRPC mutation
  const trpcMutation = useUpdateIssueAssessmentTRPC();

  const updateIssueAssessment = async (
    variables: UpdateIssueAssessmentMutationVariables & {
      ParentIssueId: string;
    }
  ): Promise<UpdateIssueAssessmentMutation> => {
    if (trpcEnabled) {
      // OriginalTimestamp is intentionally NOT forwarded to the tRPC path —
      // optimistic concurrency lives on the legacy GraphQL path only (matches
      // every other migrated update). Strip it from the spread so the tRPC
      // input shape is explicit and compiler-enforced.
      const { OriginalTimestamp: _OriginalTimestamp, ...rest } = variables;
      const trpcInput: UpdateIssueAssessmentTRPCInput = {
        ...rest,
        TagTypeIds: coerceToArray(rest.TagTypeIds),
        DepartmentTypeIds: coerceToArray(rest.DepartmentTypeIds),
        RegulationsBreachedIds: coerceToArray(rest.RegulationsBreachedIds),
        AssociatedControlIds: coerceToArray(rest.AssociatedControlIds),
        PoliciesBreachedIds: coerceToArray(rest.PoliciesBreachedIds),
      };

      return trpcMutation.updateIssueAssessment(trpcInput);
    }

    const result = await updateIssueAssessmentGraphQL({ variables });
    if (!result.data) {
      throw new Error('Failed to update issue assessment');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      updateIssueAssessment,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateIssueAssessment,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
